// The Console's reading of GUARDIUS's clock (#289).
//
// A Guardius access token's `exp` is a stamp on Guardius's clock, and the
// Console used to judge it against the browser's `Date.now()` — a clock
// Watchtower does not control. A device running AHEAD of Guardius by more than
// the token lifetime (15 min) saw every freshly minted token as expired on
// arrival and spent a refresh rotation per loop; a device running BEHIND sent
// tokens Guardius already considered expired, got a 401, and signed out.
//
// The fix is to compare `exp` against a SERVER "now": the local clock plus an
// offset learned from the token's own `iat` at the moment the token ARRIVES
// fresh from Guardius. `iat` is Guardius's clock at mint, and a token that has
// just come back from this tab's own token endpoint call was minted moments
// ago, so `iat − local now` is the clock difference to within network latency
// plus `iat`'s one-second floor. Both errors make the token look up to ~1–2 s FRESHER than it is,
// which `EXPIRY_SKEW_MS` (60 s) and `REAUTH_GUARD_MS` (5 s) absorb.
//
// WHY NOT THE HTTP `Date` HEADER: Guardius is cross-origin and `Date` is not a
// CORS-safelisted response header, so the browser hides it unless Guardius
// adds it to `Access-Control-Expose-Headers` — the same trap `Retry-After`
// sat in until Guardius 2.129.0 (#207). `iat` is in the token we already hold.
//
// NEVER LEARN FROM A STORED TOKEN. A token restored from `sessionStorage` after
// a reload was minted up to its whole lifetime ago, so `iat − now` would shift
// the clock by the token's AGE and make a token minted 14 minutes ago look
// minted now. Boot calls `loadServerOffset()` instead, which restores the value
// learned when that token arrived.
//
// NOT CLAMPED. `iat` comes from a Guardius mint whose signature the API
// verifies; a sanity bound is a possible later change, not one to invent here.
//
// `sessionStorage`, never `localStorage`: the offset belongs to this tab's
// session alongside its tokens, and the Console's `localStorage` holds exactly
// two keys by rule. Every storage access is best-effort — the in-memory value
// is the one every comparison reads, so a blocked store costs only the
// reload-survival of the offset.

const OFFSET_KEY = 'watchtower.clock.offsetMs';

let offsetMs = 0;

/** Learn the Guardius clock offset from a freshly arrived token's `iat` (in
 *  SECONDS). Anything that is not a finite number leaves the offset UNCHANGED:
 *  a token without `iat` says nothing about the clock, so it must neither
 *  erase what an earlier token taught us nor teach anything itself. */
export function learnServerOffsetFromIat(iatSeconds: unknown, localNowMs: number = Date.now()): void {
  if (typeof iatSeconds !== 'number' || !Number.isFinite(iatSeconds)) return;
  offsetMs = iatSeconds * 1000 - localNowMs;
  try {
    sessionStorage.setItem(OFFSET_KEY, String(offsetMs));
  } catch {
    // Blocked storage: the in-memory offset still serves this page's life.
  }
}

/** Guardius's "now", in ms: the local clock plus the learned offset. With
 *  nothing learned the offset is 0 and this IS the local clock — exactly the
 *  behaviour before #289. `localNowMs` is read at CALL time. */
export function serverNowMs(localNowMs: number = Date.now()): number {
  return localNowMs + offsetMs;
}

/** Restore the offset persisted when the current token arrived. Called at
 *  boot, never `learnServerOffsetFromIat` (see the header). A missing,
 *  unreadable or non-finite value is 0. */
export function loadServerOffset(): void {
  let next = 0;
  try {
    const raw = sessionStorage.getItem(OFFSET_KEY);
    const parsed = raw === null ? Number.NaN : Number(raw);
    if (Number.isFinite(parsed)) next = parsed;
  } catch {
    // Contents unknown: fall back to the local clock.
  }
  offsetMs = next;
}

/** Forget the offset. Called wherever the session's access token is cleared,
 *  so a later sign-in on this tab starts from the local clock and learns
 *  afresh from its own first token. */
export function clearServerOffset(): void {
  offsetMs = 0;
  try {
    sessionStorage.removeItem(OFFSET_KEY);
  } catch {
    // Nothing to remove that we could have read back.
  }
}
